import { ColorStyle, Colors, FusionProject } from "@/types";
import { DOWNLOAD_LINK } from "../constants";
import { Location } from "vue-router";

export function errorHandler(vue, error: any) {
  const $toast = vue.$common.utils.helper.toast;
  if (!error.message) {
    return;
  }
  const message = `${error.code || ""} ${error.message}`;
  $toast(vue, { message, color: "error" });
}

export function toast(vue: Vue, data: { message: string; color?: string }) {
  vue.$store.commit("app/SET_TOAST", { show: true, ...data });
}

export function message(
  vue: Vue,
  text: {
    title: string;
    description?: string;
    ok?: string;
    cancel?: string;
    okColor?: string;
  },
  handler: { onOK?: Function; onCancel?: Function }
) {
  vue.$root.$emit("SHOW_SIMPLE_MESSAGE_MODAL", text, handler);
}

export function toFusionPath(
  vue: Vue,
  projectName: FusionProject,
  location: Location,
  replace = false
) {
  if (replace) {
    vue.$router.replace({
      ...location,
      name: projectName + "-" + location.name
    });
  } else {
    vue.$router.push({ ...location, name: projectName + "-" + location.name });
  }
}

export function getMixinContext() {
  let ctx: any = {};
  const win: any = window;
  if (
    win.webkit &&
    win.webkit.messageHandlers &&
    win.webkit.messageHandlers.MixinContext
  ) {
    ctx = JSON.parse(prompt("MixinContext.getContext()") as any);
    ctx.platform = ctx.platform || "iOS";
  } else if (
    win.MixinContext &&
    typeof win.MixinContext.getContext === "function"
  ) {
    ctx = JSON.parse(win.MixinContext.getContext());
    ctx.platform = ctx.platform || "Android";
  }
  return ctx;
}

export function isMixin() {
  return Boolean(getMixinContext().platform);
}

export function isFoxONE() {
  return /FoxOne/i.test(navigator.userAgent);
}

export function getDefaultTheme() {
  if (isMixin()) {
    try {
      const context = getMixinContext();
      if (context) {
        return context.appearance === "dark";
      }
    } catch (error) {
      console.log(error);
    }
  }
  if (isFoxONE()) {
    return true;
  }
  return false;
}

export function getLocale() {
  let locale = navigator.language;
  if (isMixin()) {
    const context = getMixinContext();
    if (context && context.language) {
      locale = context.language;
    }
  }
  return locale.includes("zh") ? "zh" : locale.includes("ja") ? "ja" : "en";
}

export function reloadMixinTheme() {
  const w: any = window;
  let env = "";
  if (
    w.webkit &&
    w.webkit.messageHandlers &&
    w.webkit.messageHandlers.MixinContext
  ) {
    env = "iOS";
  }
  if (w.MixinContext && w.MixinContext.getContext) {
    env = "Android";
  }
  try {
    switch (env) {
      case "iOS":
        w.webkit.messageHandlers.reloadTheme.postMessage("");
        return;
      case "Android":
        w.MixinContext.reloadTheme();
    }
  } catch (error) {
    console.log(error);
  }
}

export function requestToLoginPage(vue: Vue) {
  const location = window.location.href;

  if (location.includes("basis/login")) {
    localStorage.setItem("redirect-path", "");
  } else {
    localStorage.setItem("redirect-path", location);
  }

  toFusionPath(vue, "basis", {
    name: "login"
  });
}

export function requestLogin(vue: Vue) {
  if (isFoxONE()) {
    toFusionPath(vue, "basis", {
      name: "auth-foxone"
    });
  } else {
    requestToLoginPage(vue);
  }
}

export function requestMixinLogin(vue: Vue) {
  vue.$common.utils.auth.mixin.requestCode(false, {});
}

export function requestLogout(vue: Vue) {
  vue.$store.dispatch("auth/logout");
  vue.$helper.toFusionPath(vue, "wallet", { name: "index" });
}

export function requestBindMixin(vue: Vue) {
  vue.$common.utils.auth.mixin.requestCode(false, { type: "bind" });
}

export function toFoxONEDownloadPage(locale) {
  window.location.href = DOWNLOAD_LINK[locale];
}

export function onCopySuccess(vue) {
  toast(vue, {
    message: vue.$t("common.copy.success"),
    color: "info"
  });
}

export function onCopyFail(vue) {
  toast(vue, {
    message: vue.$t("common.copy.fail"),
    color: "error"
  });
}

export function getColor(store, value) {
  const colorStyle = store.state.app.settings.colorStyle;
  if (Number(value) === 0) {
    return Colors.GRAY;
  }
  if (colorStyle === ColorStyle.GREEN_UP_RED_DOWN) {
    return Number(value) > 0 ? Colors.GREEN : Colors.RED;
  }
  return Number(value) > 0 ? Colors.RED : Colors.GREEN;
}

export function genPayment({
  amount,
  memo,
  assetId,
  recipient,
  traceId
}: {
  amount: string;
  memo: string;
  assetId: string;
  recipient: string;
  traceId: string;
}): string {
  if (isFoxONE()) {
    return `foxone://pay?amount=${amount}&asset=${assetId}&memo=${memo}&recipient=${recipient}&trace=${traceId}`;
  }
  return `mixin://pay?amount=${amount}&asset=${assetId}&memo=${memo}&recipient=${recipient}&trace=${traceId}`;
}

export function isMobile() {
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
}

export function debounce(fn: Function, delay: number) {
  let timeoutId = 0 as any;
  return (...args: any[]) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

export function throttle<T extends (...args: any[]) => any>(
  fn: T,
  limit: number
) {
  let throttling = false;
  return (...args: Parameters<T>): void | ReturnType<T> => {
    if (!throttling) {
      throttling = true;
      setTimeout(() => (throttling = false), limit);
      return fn(...args);
    }
  };
}

export function getChainLogo(vm, asset) {
  const assets = vm.$store.getters["wallet/mixin/getMergedAssets"];
  const chainAsset = assets.find(x => x.asset_id === asset.chain_id);
  return chainAsset && chainAsset.icon_url;
}

export function getAndroidVersion() {
  const ua = navigator.userAgent.toLowerCase();
  if (ua.indexOf("android") > 0) {
    const reg = /android [\d._]+/gi;
    const v_info = ua.match(reg);
    const version = (v_info + "")
      .replace(/[^0-9|_.]/gi, "")
      .replace(/_/gi, "."); // 得到版本号4.2.2
    const majorVersion = parseInt(version.split(".")[0]); // 得到版本号第一位
    return majorVersion;
  }
  return 0;
}

export function isAndroid() {
  const ua = navigator.userAgent.toLowerCase();
  return ua.includes("android");
}

export function notSupportIntersect() {
  if (isAndroid() && getAndroidVersion() < 8) {
    return true;
  }
  return !window.IntersectionObserver;
}

export function showServiceDisabled(vm: Vue) {
  message(
    vm,
    {
      title: "Service is under maintenance",
      description:
        "Please pay attention to relevant announcements, or contact customer service.",
      cancel: ""
    },
    {}
  );
}
